import { useEffect, useState } from 'react';
import AdSlot from '../../components/AdSlot';
import Link from '../../components/Link';
import { router } from '../../lib/router';
import type { BlogListResponse } from '../../types/blog';
import { ADS_ENABLED } from '../../lib/ads'; // disabled for AdSense review readiness

export default function List({ basePath }: { basePath: '/blog' | '/learn' }) {
  const [data, setData] = useState<BlogListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const search = new URLSearchParams((typeof window !== 'undefined' ? window.location.search.substring(1) : '') || '');
  const tag = search.get('tag') || '';
  const q = search.get('q') || '';

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const prefix = basePath === '/learn' ? 'learn/' : '';
    fetch(`/api/blog?${new URLSearchParams({ tag, q, ...(prefix ? { prefix } : {}) })}`)
      .then(r => r.json())
      .then((j: BlogListResponse) => { if (!aborted) { setData(j); setError(null); } })
      .catch(() => { if (!aborted) setError('Failed to load'); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [tag, q, basePath]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Blog — PrintQR';
      setMeta('description', 'Guides, best practices, and updates from PrintQR');
      setOG('og:title', 'PrintQR Blog');
      setOG('og:description', 'Guides, best practices, and updates from PrintQR');
      setLinkCanonical(basePath);
    }
  }, [basePath]);

  return (
    <div className="container" style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 12 }}>PrintQR Blog</h1>
      {/* Ads are disabled by default for AdSense review readiness */}
      {ADS_ENABLED && import.meta.env.PROD && (
        <AdSlot slotId="list-top" mode="network" size="banner" className="my-6" />
      )}
      <Filters activeTag={tag} query={q} basePath={basePath} />
      {loading && <div>Loading…</div>}
      {error && <div>{error}</div>}
      <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
        {data?.items.map((p) => {
          const heroSrc = p.hero_url ?? '';
          const href = urlFromPostId(p.id);
          return (
          <article key={p.id} className="card" style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {heroSrc && (
              <img src={heroSrc} alt="" loading="lazy" style={{ width: '100%', height: 'auto', display: 'block' }} />
            )}
            <div style={{ padding: 16 }}>
              <h2 style={{ margin: '0 0 8px' }}>
                <Link to={href}>{p.title}</Link>
              </h2>
              {p.excerpt && <p style={{ margin: '0 0 8px' }}>{p.excerpt}</p>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {p.tags?.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                  <Link key={t} to={`${basePath}?tag=${encodeURIComponent(t)}`} className="chip">#{t}</Link>
                ))}
              </div>
            </div>
          </article>
        );})}
      </div>
      {ADS_ENABLED && import.meta.env.PROD && (
        <AdSlot slotId="list-bottom" mode="network" size="banner" className="my-8" />
      )}
    </div>
  );
}

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setOG(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function Filters({ activeTag, query, basePath }: { activeTag: string; query: string; basePath: '/blog' | '/learn' }) {
  const [q, setQ] = useState(query);
  useEffect(() => setQ(query), [query]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); router.navigate(`${basePath}?q=${encodeURIComponent(q)}`); }} style={{ margin: '12px 0 24px', display: 'flex', gap: 8 }}>
      <input
        className="form-input"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search…"
        style={{ flex: 1, padding: 8 }}
      />
      <button className="btn">Search</button>
      {activeTag && <Link className="btn" to={basePath}>Clear tag</Link>}
    </form>
  );
}

function setLinkCanonical(path: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const href = `${location.origin}${path}`;
  if (!el) { el = document.createElement('link'); el.rel = 'canonical'; document.head.appendChild(el); }
  el.href = href;
}

function urlFromPostId(id: string) {
  if (id.startsWith('learn/')) return `/${id}`;
  return `/blog/${id}`;
}


